/**
 * DNACore V4.17 — ResizableTextBox (TipTap Custom Node)
 * Caixa de texto flutuante (estilo Word/Powerpoint): draggable + resizable + editável inline.
 * Persistida como <div data-textbox="1"> com atributos data-* para sobreviver a save/load
 * e exports (PDF/DOCX). Sempre flutuante (position: absolute).
 */
import React, { useRef, useState, useEffect } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import {
  ArrowUpToLine, ArrowDownToLine, Trash2, Move, RotateCw,
  Bold, Italic, AlignLeft, AlignCenter, AlignRight,
} from 'lucide-react';

const DEFAULTS = {
  width: 260,
  height: 90,
  x: 60,
  y: 60,
  rotation: 0,
  zIndex: 2,
  bg: '#ffffff',
  border: '#cbd5e1',
  align: 'left',
  fontSize: 14,
  bold: false,
  italic: false,
  text: 'Nova caixa de texto',
};

const TextBoxView = ({ node, updateAttributes, deleteNode, selected }) => {
  const a = node.attrs;
  const wrapRef = useRef(null);
  const textRef = useRef(null);
  const [drag, setDrag] = useState(null);
  const [resize, setResize] = useState(null);
  const [rot, setRot] = useState(null);

  const width = Number(a.width) || DEFAULTS.width;
  const height = Number(a.height) || DEFAULTS.height;
  const rotation = Number(a.rotation) || 0;
  const zIndex = Number(a.zIndex) || DEFAULTS.zIndex;

  // ─── Drag ───
  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      updateAttributes({ x: drag.origX + dx, y: drag.origY + dy });
    };
    const onUp = () => setDrag(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [drag, updateAttributes]);

  // ─── Resize ───
  useEffect(() => {
    if (!resize) return;
    const onMove = (e) => {
      const dx = e.clientX - resize.startX;
      const dy = e.clientY - resize.startY;
      const w = Math.max(80, resize.origW + dx);
      const h = Math.max(30, resize.origH + dy);
      updateAttributes({ width: w, height: h });
    };
    const onUp = () => setResize(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resize, updateAttributes]);

  // ─── Rotate ───
  useEffect(() => {
    if (!rot) return;
    const onMove = (e) => {
      const dx = e.clientX - rot.cx;
      const dy = e.clientY - rot.cy;
      const deg = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
      updateAttributes({ rotation: Math.round(deg) });
    };
    const onUp = () => setRot(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [rot, updateAttributes]);

  const startDrag = (e) => {
    e.preventDefault();
    setDrag({ startX: e.clientX, startY: e.clientY, origX: Number(a.x) || 0, origY: Number(a.y) || 0 });
  };
  const startResize = (e) => {
    e.preventDefault(); e.stopPropagation();
    setResize({ startX: e.clientX, startY: e.clientY, origW: width, origH: height });
  };
  const startRotate = (e) => {
    e.preventDefault(); e.stopPropagation();
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    setRot({ cx: r.left + r.width / 2, cy: r.top + r.height / 2 });
  };

  const commitText = () => {
    const el = textRef.current;
    if (!el) return;
    const txt = el.innerText || '';
    if (txt !== a.text) updateAttributes({ text: txt });
  };

  const toFront = () => updateAttributes({ zIndex: (Number(a.zIndex) || 1) + 1 });
  const toBack  = () => updateAttributes({ zIndex: Math.max(0, (Number(a.zIndex) || 1) - 1) });
  const setAlign = (v) => updateAttributes({ align: v });
  const toggleBold = () => updateAttributes({ bold: !a.bold });
  const toggleItalic = () => updateAttributes({ italic: !a.italic });

  const style = {
    position: 'absolute',
    left: `${Number(a.x) || 0}px`,
    top: `${Number(a.y) || 0}px`,
    width: `${width}px`,
    height: `${height}px`,
    transform: rotation ? `rotate(${rotation}deg)` : undefined,
    zIndex,
    background: a.bg || DEFAULTS.bg,
    border: `1px solid ${a.border || DEFAULTS.border}`,
    borderRadius: 4,
    boxShadow: selected ? '0 0 0 2px rgba(124,58,237,0.35)' : '0 1px 3px rgba(15,23,42,0.08)',
    boxSizing: 'border-box',
    padding: 6,
    cursor: drag ? 'grabbing' : 'default',
    userSelect: drag ? 'none' : 'auto',
  };

  const innerStyle = {
    width: '100%',
    height: '100%',
    outline: 'none',
    fontSize: `${a.fontSize || DEFAULTS.fontSize}px`,
    textAlign: a.align || DEFAULTS.align,
    fontWeight: a.bold ? 700 : 400,
    fontStyle: a.italic ? 'italic' : 'normal',
    color: '#0f172a',
    whiteSpace: 'pre-wrap',
    overflow: 'hidden',
  };

  const tbBtn = (onClick) => ({
    onClick,
    onMouseDown: (e) => e.preventDefault(),
    type: 'button',
  });

  return (
    <NodeViewWrapper as="span" className="ae-tb-wrap" contentEditable={false}>
      <div
        ref={wrapRef}
        className={`ae-tb ${selected ? 'is-selected' : ''}`}
        style={style}
        data-testid={`ae-textbox-${a.x}-${a.y}`}
      >
        {/* área de arrasto na borda topo */}
        <span
          className="ae-tb-drag"
          onMouseDown={startDrag}
          title="Arrastar caixa de texto"
          style={{
            position: 'absolute', left: 0, right: 0, top: -14, height: 12,
            background: 'rgba(124,58,237,0.85)', color: '#fff', fontSize: 9,
            display: selected ? 'flex' : 'none', alignItems: 'center', justifyContent: 'center',
            gap: 4, borderTopLeftRadius: 4, borderTopRightRadius: 4, cursor: 'grab',
          }}
        >
          <Move size={9} /> arraste
        </span>

        {/* conteúdo editável */}
        <div
          ref={textRef}
          contentEditable
          suppressContentEditableWarning
          onBlur={commitText}
          onMouseDown={(e) => e.stopPropagation()}
          style={innerStyle}
        >
          {a.text || ''}
        </div>

        {selected && (
          <>
            {/* handle de resize (canto inferior direito) */}
            <span
              onMouseDown={startResize}
              title="Redimensionar"
              style={{
                position: 'absolute', right: -6, bottom: -6, width: 12, height: 12,
                background: '#7B3F98', border: '2px solid #fff', borderRadius: 2,
                cursor: 'nwse-resize', boxShadow: '0 2px 4px rgba(0,0,0,0.2)', zIndex: 5,
              }}
            />
            {/* rotate */}
            <span
              onMouseDown={startRotate}
              title="Rotacionar"
              style={{
                position: 'absolute', top: -22, left: '50%', transform: 'translateX(-50%)',
                width: 18, height: 18, background: '#7B3F98', border: '2px solid #fff',
                borderRadius: '50%', cursor: 'grab', display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center', color: '#fff', zIndex: 5,
              }}
            ><RotateCw size={9} /></span>
            {/* mini-toolbar */}
            <span
              contentEditable={false}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                position: 'absolute', top: -34, right: 0,
                display: 'flex', gap: 1, background: '#1f2937', borderRadius: 6, padding: 3,
                boxShadow: '0 4px 14px rgba(0,0,0,0.25)', zIndex: 6,
              }}
            >
              <button {...tbBtn(toggleBold)}     title="Negrito"    style={{ background: a.bold ? '#7B3F98' : 'transparent', border: 0, color: '#e5e7eb', width: 22, height: 22, borderRadius: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} data-testid="tb-bold"><Bold size={11} /></button>
              <button {...tbBtn(toggleItalic)}   title="Itálico"    style={{ background: a.italic ? '#7B3F98' : 'transparent', border: 0, color: '#e5e7eb', width: 22, height: 22, borderRadius: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} data-testid="tb-italic"><Italic size={11} /></button>
              <button {...tbBtn(() => setAlign('left'))}   title="Esquerda"  style={{ background: a.align === 'left'   ? '#7B3F98' : 'transparent', border: 0, color: '#e5e7eb', width: 22, height: 22, borderRadius: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} data-testid="tb-al"><AlignLeft size={11} /></button>
              <button {...tbBtn(() => setAlign('center'))} title="Centro"    style={{ background: a.align === 'center' ? '#7B3F98' : 'transparent', border: 0, color: '#e5e7eb', width: 22, height: 22, borderRadius: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} data-testid="tb-ac"><AlignCenter size={11} /></button>
              <button {...tbBtn(() => setAlign('right'))}  title="Direita"   style={{ background: a.align === 'right'  ? '#7B3F98' : 'transparent', border: 0, color: '#e5e7eb', width: 22, height: 22, borderRadius: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} data-testid="tb-ar"><AlignRight size={11} /></button>
              <button {...tbBtn(toFront)} title="Frente"       style={{ background: 'transparent', border: 0, color: '#e5e7eb', width: 22, height: 22, borderRadius: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} data-testid="tb-front"><ArrowUpToLine size={11} /></button>
              <button {...tbBtn(toBack)}  title="Trás"         style={{ background: 'transparent', border: 0, color: '#e5e7eb', width: 22, height: 22, borderRadius: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} data-testid="tb-back"><ArrowDownToLine size={11} /></button>
              <button {...tbBtn(() => deleteNode())} title="Excluir" style={{ background: 'transparent', border: 0, color: '#fca5a5', width: 22, height: 22, borderRadius: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} data-testid="tb-delete"><Trash2 size={11} /></button>
            </span>
          </>
        )}
      </div>
    </NodeViewWrapper>
  );
};

// ─── TipTap Node Extension ───
export const ResizableTextBox = Node.create({
  name: 'textbox',
  group: 'block',
  atom: true,
  draggable: false,
  selectable: true,

  addAttributes() {
    return {
      text:      { default: DEFAULTS.text,      parseHTML: el => el.getAttribute('data-text') || el.innerText || DEFAULTS.text },
      x:         { default: DEFAULTS.x,         parseHTML: el => Number(el.getAttribute('data-x') || DEFAULTS.x) || 0 },
      y:         { default: DEFAULTS.y,         parseHTML: el => Number(el.getAttribute('data-y') || DEFAULTS.y) || 0 },
      width:     { default: DEFAULTS.width,     parseHTML: el => Number(el.getAttribute('data-w') || DEFAULTS.width) || DEFAULTS.width },
      height:    { default: DEFAULTS.height,    parseHTML: el => Number(el.getAttribute('data-h') || DEFAULTS.height) || DEFAULTS.height },
      rotation:  { default: DEFAULTS.rotation,  parseHTML: el => Number(el.getAttribute('data-rot') || 0) || 0 },
      zIndex:    { default: DEFAULTS.zIndex,    parseHTML: el => Number(el.getAttribute('data-z') || DEFAULTS.zIndex) || DEFAULTS.zIndex },
      bg:        { default: DEFAULTS.bg,        parseHTML: el => el.getAttribute('data-bg') || DEFAULTS.bg },
      border:    { default: DEFAULTS.border,    parseHTML: el => el.getAttribute('data-border') || DEFAULTS.border },
      align:     { default: DEFAULTS.align,     parseHTML: el => el.getAttribute('data-align') || DEFAULTS.align },
      fontSize:  { default: DEFAULTS.fontSize,  parseHTML: el => Number(el.getAttribute('data-fs') || DEFAULTS.fontSize) || DEFAULTS.fontSize },
      bold:      { default: DEFAULTS.bold,      parseHTML: el => el.getAttribute('data-b') === '1' },
      italic:    { default: DEFAULTS.italic,    parseHTML: el => el.getAttribute('data-i') === '1' },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-textbox="1"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const {
      text, x, y, width, height, rotation, zIndex, bg, border, align, fontSize, bold, italic, ...rest
    } = HTMLAttributes;
    const w = Number(width) || DEFAULTS.width;
    const h = Number(height) || DEFAULTS.height;
    const rot = Number(rotation) || 0;
    const z = Number(zIndex) || DEFAULTS.zIndex;
    const styleParts = [
      'position:absolute',
      `left:${Number(x) || 0}px`,
      `top:${Number(y) || 0}px`,
      `width:${w}px`,
      `height:${h}px`,
      rot ? `transform:rotate(${rot}deg)` : '',
      `z-index:${z}`,
      `background:${bg || DEFAULTS.bg}`,
      `border:1px solid ${border || DEFAULTS.border}`,
      'border-radius:4px',
      'padding:6px',
      'box-sizing:border-box',
      `text-align:${align || DEFAULTS.align}`,
      `font-size:${Number(fontSize) || DEFAULTS.fontSize}px`,
      bold ? 'font-weight:700' : '',
      italic ? 'font-style:italic' : '',
      'white-space:pre-wrap',
      'overflow:hidden',
    ].filter(Boolean).join(';');
    return ['div', mergeAttributes(rest, {
      'data-textbox': '1',
      'data-text': text || '',
      'data-x': String(Number(x) || 0),
      'data-y': String(Number(y) || 0),
      'data-w': String(w),
      'data-h': String(h),
      'data-rot': String(rot),
      'data-z': String(z),
      'data-bg': bg || DEFAULTS.bg,
      'data-border': border || DEFAULTS.border,
      'data-align': align || DEFAULTS.align,
      'data-fs': String(Number(fontSize) || DEFAULTS.fontSize),
      'data-b': bold ? '1' : '0',
      'data-i': italic ? '1' : '0',
      style: styleParts,
    }), text || ''];
  },

  addNodeView() {
    return ReactNodeViewRenderer(TextBoxView);
  },

  addCommands() {
    return {
      insertTextBox: (opts = {}) => ({ commands }) =>
        commands.insertContent({ type: this.name, attrs: { ...DEFAULTS, ...opts } }),
    };
  },
});

export default ResizableTextBox;
